import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Progress } from '../../components/ui/progress';
import { Checkbox } from '../../components/ui/checkbox';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import {
  ArrowLeft, Brain, Sparkles, ShieldAlert, Database, FileText, ClipboardCheck,
  Building2, Loader2, Save, ExternalLink, CheckCircle, XCircle, Eye,
  Users, Globe, FileDown, FileBox,
} from 'lucide-react';
import { aiActApi } from '../../lib/api';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const RISK_BADGE = {
  prohibited: { label: 'Proibido', color: 'bg-red-100 text-red-800 border-red-300' },
  high: { label: 'Alto Risco', color: 'bg-orange-100 text-orange-800 border-orange-300' },
  limited: { label: 'Risco Limitado', color: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
  minimal: { label: 'Risco Mínimo', color: 'bg-green-100 text-green-800 border-green-300' },
};

const TECH_SECTIONS = [
  { key: 'section_1_general', label: '1. Descrição geral do sistema' },
  { key: 'section_2_instructions', label: '2. Instruções de utilização' },
  { key: 'section_3_architecture', label: '3. Arquitetura e componentes' },
  { key: 'section_4_training_data', label: '4. Dados de treino, validação e teste' },
  { key: 'section_5_risk_assessment', label: '5. Avaliação de risco' },
  { key: 'section_6_testing', label: '6. Estratégia de teste' },
  { key: 'section_7_performance', label: '7. Métricas de desempenho' },
  { key: 'section_8_standards', label: '8. Normas harmonizadas aplicadas' },
  { key: 'section_9_declaration', label: '9. Declaração de conformidade' },
];

export default function AiSystemDetail() {
  const { id } = useParams();
  const [system, setSystem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [risks, setRisks] = useState([]);
  const [dataGov, setDataGov] = useState(null);
  const [techDoc, setTechDoc] = useState(null);
  const [conformity, setConformity] = useState(null);
  const [gpai, setGpai] = useState(null);
  const [transparency, setTransparency] = useState(null);
  const [hOversight, setHOversight] = useState(null);
  const [euRegistry, setEuRegistry] = useState(null);
  const [savingTech, setSavingTech] = useState(false);
  const [savingGov, setSavingGov] = useState(false);
  const [savingGpai, setSavingGpai] = useState(false);
  const [savingTrans, setSavingTrans] = useState(false);
  const [savingOversight, setSavingOversight] = useState(false);
  const [savingEuReg, setSavingEuReg] = useState(false);
  const [submittingEu, setSubmittingEu] = useState(false);
  const [draftingTech, setDraftingTech] = useState(false);
  const [draftingTrans, setDraftingTrans] = useState(false);
  const [techOverwrite, setTechOverwrite] = useState(false);
  const [transOverwrite, setTransOverwrite] = useState(false);
  const [techDraftInfo, setTechDraftInfo] = useState(null);
  const [transDraftInfo, setTransDraftInfo] = useState(null);

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [id]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [sys, rks, dg, td, cf, tr, ho, eu] = await Promise.all([
        aiActApi.getSystem(id),
        aiActApi.listRisks({ system_id: id }),
        aiActApi.getDataGov(id),
        aiActApi.getTechDoc(id),
        aiActApi.getConformity(id),
        aiActApi.getTransparency(id),
        aiActApi.getHumanOversight(id),
        aiActApi.getEuRegistry(id),
      ]);
      setSystem(sys.data);
      setRisks(rks.data);
      setDataGov(dg.data);
      setTechDoc(td.data);
      setConformity(cf.data);
      setTransparency(tr.data);
      setHOversight(ho.data);
      setEuRegistry(eu.data);
      if (sys.data.entity_type === 'gpai_model') {
        try { const g = await aiActApi.getGpai(id); setGpai(g.data); } catch { /* ignore */ }
      }
    } catch { toast.error('Erro ao carregar sistema IA'); }
    finally { setLoading(false); }
  };

  const saveTransparency = async () => {
    setSavingTrans(true);
    try {
      const res = await aiActApi.saveTransparency(id, transparency);
      setTransparency(res.data); toast.success('Ficha de transparência guardada'); loadConformity();
    } catch { toast.error('Erro ao guardar transparência'); }
    finally { setSavingTrans(false); }
  };

  const saveOversight = async () => {
    setSavingOversight(true);
    try {
      const res = await aiActApi.saveHumanOversight(id, hOversight);
      setHOversight(res.data); toast.success(`Supervisão guardada (${res.data.completeness_score}%)`); loadConformity();
    } catch { toast.error('Erro ao guardar supervisão'); }
    finally { setSavingOversight(false); }
  };

  const saveEuReg = async () => {
    setSavingEuReg(true);
    try {
      const res = await aiActApi.saveEuRegistry(id, euRegistry);
      setEuRegistry(res.data); toast.success('Registo EU guardado');
    } catch { toast.error('Erro ao guardar registo EU'); }
    finally { setSavingEuReg(false); }
  };

  const submitEuReg = async () => {
    setSubmittingEu(true);
    try {
      await aiActApi.submitEuRegistry(id);
      toast.success('Submetido para EU Database');
      loadAll();
    } catch { toast.error('Erro ao submeter EU Registry'); }
    finally { setSubmittingEu(false); }
  };

  const autoDraftTechDoc = async () => {
    setDraftingTech(true);
    setTechDraftInfo(null);
    try {
      const res = await aiActApi.autoDraftTechDoc(id, { language: 'pt-PT', overwrite: techOverwrite });
      const proposed = res.data.proposed_sections || {};
      setTechDoc({ ...techDoc, ...proposed });
      setTechDraftInfo({
        filled: res.data.filled_count,
        skipped: res.data.skipped_existing_sections || [],
        keys: Object.keys(proposed),
      });
      toast.success(`${res.data.filled_count} secções pré-preenchidas por IA — reveja antes de guardar`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro no auto-draft IA'); }
    finally { setDraftingTech(false); }
  };

  const autoDraftTransparency = async () => {
    setDraftingTrans(true);
    setTransDraftInfo(null);
    try {
      const res = await aiActApi.autoDraftTransparency(id, { language: 'pt-PT', overwrite: transOverwrite });
      const proposed = res.data.proposed_fields || {};
      setTransparency({ ...transparency, ...proposed });
      setTransDraftInfo({
        filled: res.data.filled_count,
        skipped: res.data.skipped_existing_fields || [],
        keys: Object.keys(proposed),
      });
      toast.success(`${res.data.filled_count} campos pré-preenchidos por IA — reveja antes de guardar`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro no auto-draft IA'); }
    finally { setDraftingTrans(false); }
  };

  const saveTechDoc = async () => {
    setSavingTech(true);
    try {
      const res = await aiActApi.saveTechDoc(id, techDoc);
      setTechDoc(res.data);
      toast.success(`Documentação guardada (${res.data.completeness_score}% completa)`);
      loadConformity();
    } catch { toast.error('Erro ao guardar documentação'); }
    finally { setSavingTech(false); }
  };

  const saveDataGov = async () => {
    setSavingGov(true);
    try {
      // Build section scores
      const sections = {
        origin: dataGov?.data_origin ? 20 : 0,
        purpose: dataGov?.training_purpose ? 15 : 0,
        quality: dataGov?.validation_cleaning ? 20 : 0,
        representativeness: dataGov?.representativeness ? 15 : 0,
        bias: dataGov?.bias_analysis_method ? 15 : 0,
        privacy: (dataGov?.rgpd_legal_basis || !dataGov?.personal_data) ? 15 : 0,
      };
      const payload = { ...dataGov, section_scores: sections };
      const res = await aiActApi.saveDataGov(id, payload);
      setDataGov(res.data);
      toast.success(`Governança guardada (score ${res.data.total_score}/100)`);
      loadConformity();
    } catch { toast.error('Erro ao guardar governança de dados'); }
    finally { setSavingGov(false); }
  };

  const saveGpai = async () => {
    setSavingGpai(true);
    try {
      const res = await aiActApi.saveGpai(id, gpai);
      setGpai(res.data);
      toast.success('Detalhes GPAI guardados');
    } catch { toast.error('Erro ao guardar GPAI'); }
    finally { setSavingGpai(false); }
  };

  const loadConformity = async () => {
    try { const cf = await aiActApi.getConformity(id); setConformity(cf.data); } catch { /* ignore */ }
  };

  if (loading) return <Layout title="Sistema IA"><div className="flex justify-center h-64 items-center"><Loader2 className="h-8 w-8 animate-spin" /></div></Layout>;
  if (!system) return <Layout title="Sistema IA"><div className="text-center py-12 text-slate-500">Sistema não encontrado</div></Layout>;

  const riskCfg = system.ai_risk_level ? RISK_BADGE[system.ai_risk_level] : null;

  const header = (
    <div className="flex items-center gap-2">
      <a href={aiActApi.pdfSummary(id)} target="_blank" rel="noopener noreferrer" data-testid="pdf-summary-btn">
        <Button variant="outline"><FileDown className="h-4 w-4 mr-2" />PDF Sumário</Button>
      </a>
      <a href={aiActApi.pdfAnnexIV(id)} target="_blank" rel="noopener noreferrer" data-testid="pdf-annex-iv-btn">
        <Button variant="outline"><FileBox className="h-4 w-4 mr-2" />PDF Anexo IV</Button>
      </a>
      <Link to={`/ai-act/systems/${id}/classify`}>
        <Button data-testid="goto-classify-btn"><Sparkles className="h-4 w-4 mr-2" />Wizard Classificação</Button>
      </Link>
    </div>
  );

  return (
    <Layout title={system.name} headerContent={header}>
      <div className="space-y-6" data-testid="ai-system-detail">

        <Link to="/ai-act/systems" className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900">
          <ArrowLeft className="h-4 w-4 mr-1" />Voltar aos sistemas IA
        </Link>

        {/* Header card */}
        <Card className="card-flat">
          <CardContent className="p-5">
            <div className="flex items-start gap-4">
              <div className="h-14 w-14 rounded-lg bg-indigo-100 text-indigo-600 flex items-center justify-center shrink-0">
                <Brain className="h-7 w-7" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className="font-mono text-xs text-slate-400">{system.system_id}</span>
                  {system.entity_type === 'gpai_model' && (
                    <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200">GPAI</Badge>
                  )}
                  {riskCfg ? (
                    <Badge variant="outline" className={cn(riskCfg.color)} data-testid="risk-level-badge">{riskCfg.label}</Badge>
                  ) : (
                    <Badge variant="outline" className="bg-slate-100">Não classificado</Badge>
                  )}
                </div>
                <h2 className="text-2xl font-bold text-slate-900">{system.name}</h2>
                <p className="text-sm text-slate-600 mt-1">{system.provider} {system.version && `• v${system.version}`}</p>
                {system.ai_purpose && <p className="text-sm text-slate-700 mt-3">{system.ai_purpose}</p>}
                {system.ai_risk_basis && (
                  <p className="text-xs text-slate-500 mt-3 italic">Base legal: {system.ai_risk_basis}</p>
                )}
              </div>
              {conformity && (
                <div className="text-center shrink-0">
                  <div className="text-3xl font-bold text-indigo-600" data-testid="readiness-score">{conformity.readiness_score}%</div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Conformidade</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="conformity" className="space-y-4">
          <TabsList className="grid grid-cols-3 md:grid-cols-8 gap-1" data-testid="system-tabs">
            <TabsTrigger value="conformity" data-testid="tab-conformity"><ClipboardCheck className="h-4 w-4 mr-1" />Conformidade</TabsTrigger>
            <TabsTrigger value="risks" data-testid="tab-risks"><ShieldAlert className="h-4 w-4 mr-1" />Riscos ({risks.length})</TabsTrigger>
            <TabsTrigger value="data-gov" data-testid="tab-data-gov"><Database className="h-4 w-4 mr-1" />Dados</TabsTrigger>
            <TabsTrigger value="tech-doc" data-testid="tab-tech-doc"><FileText className="h-4 w-4 mr-1" />Doc Técnica</TabsTrigger>
            <TabsTrigger value="transparency" data-testid="tab-transparency"><Eye className="h-4 w-4 mr-1" />Transparência</TabsTrigger>
            <TabsTrigger value="oversight" data-testid="tab-oversight"><Users className="h-4 w-4 mr-1" />Supervisão</TabsTrigger>
            <TabsTrigger value="eu-registry" data-testid="tab-eu-registry"><Globe className="h-4 w-4 mr-1" />Registo EU</TabsTrigger>
            {system.entity_type === 'gpai_model' && (
              <TabsTrigger value="gpai" data-testid="tab-gpai"><Brain className="h-4 w-4 mr-1" />GPAI</TabsTrigger>
            )}
          </TabsList>

          {/* Conformity */}
          <TabsContent value="conformity">
            <Card className="card-flat">
              <CardHeader><CardTitle>Auto-avaliação de Conformidade (F9)</CardTitle></CardHeader>
              <CardContent>
                <div className="mb-4">
                  <Progress value={conformity?.readiness_score ?? 0} />
                  <p className="text-sm text-slate-600 mt-2">
                    {conformity?.conforme_count ?? 0}/{conformity?.total_controls ?? 0} controlos conformes
                  </p>
                </div>
                <div className="space-y-2" data-testid="conformity-controls">
                  {(conformity?.controls || []).map(c => (
                    <div key={c.code} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        {c.status === 'conforme' ? <CheckCircle className="h-5 w-5 text-green-600" /> :
                         c.status === 'parcial' ? <ClipboardCheck className="h-5 w-5 text-amber-600" /> :
                         <XCircle className="h-5 w-5 text-slate-400" />}
                        <span className="text-sm font-medium text-slate-800">{c.title}</span>
                      </div>
                      <Badge variant="outline" className={cn(
                        'text-xs',
                        c.status === 'conforme' ? 'bg-green-50 text-green-700 border-green-200' :
                        c.status === 'parcial' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                        'bg-slate-100 text-slate-600 border-slate-200'
                      )}>{c.status}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Risks */}
          <TabsContent value="risks">
            <Card className="card-flat">
              <CardHeader>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <CardTitle>Registo de Riscos IA (F3 — Art. 9)</CardTitle>
                  <div className="flex items-center gap-2">
                    <Link to={`/ai-act/risks?system_id=${id}&ai=1`}>
                      <Button variant="outline" size="sm" data-testid="ai-suggest-from-detail">
                        <Sparkles className="h-4 w-4 mr-1 text-indigo-600" />Sugestões IA
                      </Button>
                    </Link>
                    <Link to={`/ai-act/risks?system_id=${id}`}>
                      <Button variant="outline" size="sm" data-testid="goto-risks-btn">Gerir riscos<ExternalLink className="h-3 w-3 ml-1" /></Button>
                    </Link>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {risks.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-6">Nenhum risco registado para este sistema</p>
                ) : (
                  <div className="space-y-2">
                    {risks.map(r => (
                      <div key={r.ai_risk_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-testid={`risk-row-${r.ai_risk_id}`}>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-mono text-xs text-slate-400">{r.ai_risk_id}</span>
                            <Badge variant="outline" className="text-xs">{r.risk_category}</Badge>
                            <Badge variant="outline" className={cn('text-xs',
                              r.risk_score >= 20 ? 'bg-red-50 text-red-700 border-red-200' :
                              r.risk_score >= 10 ? 'bg-orange-50 text-orange-700 border-orange-200' :
                              'bg-green-50 text-green-700 border-green-200'
                            )}>Score {r.risk_score}</Badge>
                          </div>
                          <p className="text-sm text-slate-800">{r.risk_description}</p>
                        </div>
                        <Badge variant="outline" className="text-xs ml-2">{r.status}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Data Governance */}
          <TabsContent value="data-gov">
            <Card className="card-flat" data-testid="data-gov-form">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Governança de Dados (F4 — Art. 10)</CardTitle>
                  <span className="text-sm font-semibold text-indigo-600">Score: {dataGov?.total_score ?? 0}/100</span>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Origem dos dados</Label>
                  <Textarea rows={2} value={dataGov?.data_origin || ''} onChange={e => setDataGov({ ...dataGov, data_origin: e.target.value })} data-testid="dg-origin" />
                </div>
                <div className="space-y-2">
                  <Label>Finalidade do treino</Label>
                  <Textarea rows={2} value={dataGov?.training_purpose || ''} onChange={e => setDataGov({ ...dataGov, training_purpose: e.target.value })} data-testid="dg-purpose" />
                </div>
                <div className="space-y-2">
                  <Label>Validação e limpeza de dados</Label>
                  <Textarea rows={2} value={dataGov?.validation_cleaning || ''} onChange={e => setDataGov({ ...dataGov, validation_cleaning: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Representatividade</Label>
                  <Textarea rows={2} value={dataGov?.representativeness || ''} onChange={e => setDataGov({ ...dataGov, representativeness: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Método de análise de viés (bias)</Label>
                  <Textarea rows={2} value={dataGov?.bias_analysis_method || ''} onChange={e => setDataGov({ ...dataGov, bias_analysis_method: e.target.value })} />
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox id="personal_data" checked={!!dataGov?.personal_data} onCheckedChange={(c) => setDataGov({ ...dataGov, personal_data: !!c })} data-testid="dg-personal" />
                  <Label htmlFor="personal_data">Trata dados pessoais (RGPD aplicável)</Label>
                </div>
                {dataGov?.personal_data && (
                  <>
                    <div className="space-y-2">
                      <Label>Referência DPIA</Label>
                      <Input value={dataGov?.dpia_ref || ''} onChange={e => setDataGov({ ...dataGov, dpia_ref: e.target.value })} placeholder="DPIA-2025-001" />
                    </div>
                    <div className="space-y-2">
                      <Label>Base legal RGPD</Label>
                      <Input value={dataGov?.rgpd_legal_basis || ''} onChange={e => setDataGov({ ...dataGov, rgpd_legal_basis: e.target.value })} placeholder="Art. 6(1)(a) consentimento / 6(1)(f) interesse legítimo" />
                    </div>
                  </>
                )}
                <div className="space-y-2">
                  <Label>Política de retenção</Label>
                  <Input value={dataGov?.retention_policy || ''} onChange={e => setDataGov({ ...dataGov, retention_policy: e.target.value })} />
                </div>
                <Button onClick={saveDataGov} disabled={savingGov} data-testid="save-data-gov-btn">
                  {savingGov ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  Guardar Governança
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tech Doc */}
          <TabsContent value="tech-doc">
            <Card className="card-flat" data-testid="tech-doc-form">
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <CardTitle>Documentação Técnica (F5 — Anexo IV)</CardTitle>
                  <span className="text-sm font-semibold text-indigo-600">{techDoc?.completeness_score ?? 0}% completo</span>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-4 space-y-3" data-testid="tech-autodraft-panel">
                  <div className="flex items-start gap-3">
                    <Sparkles className="h-5 w-5 text-indigo-600 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-indigo-900">Auto-draft com IA (Motor de IA)</p>
                      <p className="text-sm text-indigo-700">
                        Gera rascunho profissional das 9 secções com base na classificação, governança de dados e riscos registados.
                        Use placeholders <code>[a preencher: …]</code> onde faltar info concreta.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                      <Checkbox checked={techOverwrite} onCheckedChange={c => setTechOverwrite(!!c)} data-testid="tech-overwrite-toggle" />
                      Sobrescrever secções já preenchidas
                    </label>
                    <Button onClick={autoDraftTechDoc} disabled={draftingTech} className="ml-auto" data-testid="tech-autodraft-btn">
                      {draftingTech ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" />A gerar rascunho (~20s)...</>
                      ) : (
                        <><Sparkles className="h-4 w-4 mr-2" />Gerar rascunho IA</>
                      )}
                    </Button>
                  </div>
                  {techDraftInfo && (
                    <div className="text-xs bg-white border border-indigo-100 rounded p-2 text-slate-700" data-testid="tech-draft-info">
                      <strong>{techDraftInfo.filled}</strong> secções pré-preenchidas • {techDraftInfo.skipped.length > 0 ? `${techDraftInfo.skipped.length} ignoradas (já tinham conteúdo)` : 'todas as secções vazias foram preenchidas'}. <span className="text-indigo-700 font-medium">Reveja cada secção antes de guardar.</span>
                    </div>
                  )}
                </div>
                {TECH_SECTIONS.map(s => (
                  <div key={s.key} className="space-y-2">
                    <Label className="flex items-center gap-2">
                      {s.label}
                      {techDraftInfo?.keys?.includes(s.key) && (
                        <Badge variant="outline" className="text-[10px] bg-indigo-50 text-indigo-700 border-indigo-200">
                          <Sparkles className="h-2.5 w-2.5 mr-0.5" />IA
                        </Badge>
                      )}
                    </Label>
                    <Textarea rows={3}
                      value={techDoc?.[s.key] || ''}
                      onChange={e => setTechDoc({ ...techDoc, [s.key]: e.target.value })}
                      data-testid={`tech-${s.key}`} />
                  </div>
                ))}
                <Button onClick={saveTechDoc} disabled={savingTech} data-testid="save-tech-doc-btn">
                  {savingTech ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  Guardar Documentação
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Transparency (F7) */}
          <TabsContent value="transparency">
            <Card className="card-flat" data-testid="transparency-form">
              <CardHeader>
                <CardTitle>Ficha de Transparência (F7 — Art. 13)</CardTitle>
                <p className="text-sm text-slate-600">Informação prestada ao deployer/utilizador final.</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-4 space-y-3" data-testid="trans-autodraft-panel">
                  <div className="flex items-start gap-3">
                    <Sparkles className="h-5 w-5 text-indigo-600 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-indigo-900">Auto-draft com IA (Motor de IA)</p>
                      <p className="text-sm text-indigo-700">
                        Gera rascunho da Ficha de Transparência com base na finalidade, performance e riscos do sistema.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                      <Checkbox checked={transOverwrite} onCheckedChange={c => setTransOverwrite(!!c)} data-testid="trans-overwrite-toggle" />
                      Sobrescrever campos já preenchidos
                    </label>
                    <Button onClick={autoDraftTransparency} disabled={draftingTrans} className="ml-auto" data-testid="trans-autodraft-btn">
                      {draftingTrans ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" />A gerar (~15s)...</>
                      ) : (
                        <><Sparkles className="h-4 w-4 mr-2" />Gerar rascunho IA</>
                      )}
                    </Button>
                  </div>
                  {transDraftInfo && (
                    <div className="text-xs bg-white border border-indigo-100 rounded p-2 text-slate-700" data-testid="trans-draft-info">
                      <strong>{transDraftInfo.filled}</strong> campos pré-preenchidos • {transDraftInfo.skipped.length > 0 ? `${transDraftInfo.skipped.length} ignorados (já tinham conteúdo)` : 'todos os campos vazios foram preenchidos'}. <span className="text-indigo-700 font-medium">Reveja antes de guardar.</span>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    Identidade do fornecedor
                    {transDraftInfo?.keys?.includes('provider_identity') && <Badge variant="outline" className="text-[10px] bg-indigo-50 text-indigo-700 border-indigo-200"><Sparkles className="h-2.5 w-2.5 mr-0.5" />IA</Badge>}
                  </Label>
                  <Input value={transparency?.provider_identity || ''} onChange={e => setTransparency({ ...transparency, provider_identity: e.target.value })} data-testid="tr-provider" />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    Finalidade prevista *
                    {transDraftInfo?.keys?.includes('intended_purpose') && <Badge variant="outline" className="text-[10px] bg-indigo-50 text-indigo-700 border-indigo-200"><Sparkles className="h-2.5 w-2.5 mr-0.5" />IA</Badge>}
                  </Label>
                  <Textarea rows={2} value={transparency?.intended_purpose || ''} onChange={e => setTransparency({ ...transparency, intended_purpose: e.target.value })} data-testid="tr-purpose" />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    Capacidades e limitações *
                    {transDraftInfo?.keys?.includes('capabilities_limitations') && <Badge variant="outline" className="text-[10px] bg-indigo-50 text-indigo-700 border-indigo-200"><Sparkles className="h-2.5 w-2.5 mr-0.5" />IA</Badge>}
                  </Label>
                  <Textarea rows={3} value={transparency?.capabilities_limitations || ''} onChange={e => setTransparency({ ...transparency, capabilities_limitations: e.target.value })} data-testid="tr-cap" />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    Métricas de exatidão
                    {transDraftInfo?.keys?.includes('accuracy_metrics') && <Badge variant="outline" className="text-[10px] bg-indigo-50 text-indigo-700 border-indigo-200"><Sparkles className="h-2.5 w-2.5 mr-0.5" />IA</Badge>}
                  </Label>
                  <Textarea rows={2} value={transparency?.accuracy_metrics || ''} onChange={e => setTransparency({ ...transparency, accuracy_metrics: e.target.value })} placeholder="Ex: accuracy 92%, F1 0.88, etc." />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    Riscos previsíveis para utilizadores
                    {transDraftInfo?.keys?.includes('foreseeable_risks') && <Badge variant="outline" className="text-[10px] bg-indigo-50 text-indigo-700 border-indigo-200"><Sparkles className="h-2.5 w-2.5 mr-0.5" />IA</Badge>}
                  </Label>
                  <Textarea rows={2} value={transparency?.foreseeable_risks || ''} onChange={e => setTransparency({ ...transparency, foreseeable_risks: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      Utilizadores-alvo
                      {transDraftInfo?.keys?.includes('target_users') && <Badge variant="outline" className="text-[10px] bg-indigo-50 text-indigo-700 border-indigo-200"><Sparkles className="h-2.5 w-2.5 mr-0.5" />IA</Badge>}
                    </Label>
                    <Input value={transparency?.target_users || ''} onChange={e => setTransparency({ ...transparency, target_users: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      Tempo de vida esperado
                      {transDraftInfo?.keys?.includes('expected_lifetime') && <Badge variant="outline" className="text-[10px] bg-indigo-50 text-indigo-700 border-indigo-200"><Sparkles className="h-2.5 w-2.5 mr-0.5" />IA</Badge>}
                    </Label>
                    <Input value={transparency?.expected_lifetime || ''} onChange={e => setTransparency({ ...transparency, expected_lifetime: e.target.value })} placeholder="Ex: 2 anos" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    Recursos técnicos requeridos
                    {transDraftInfo?.keys?.includes('technical_resources_required') && <Badge variant="outline" className="text-[10px] bg-indigo-50 text-indigo-700 border-indigo-200"><Sparkles className="h-2.5 w-2.5 mr-0.5" />IA</Badge>}
                  </Label>
                  <Input value={transparency?.technical_resources_required || ''} onChange={e => setTransparency({ ...transparency, technical_resources_required: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    Contacto para reclamações
                    {transDraftInfo?.keys?.includes('contact_for_complaints') && <Badge variant="outline" className="text-[10px] bg-indigo-50 text-indigo-700 border-indigo-200"><Sparkles className="h-2.5 w-2.5 mr-0.5" />IA</Badge>}
                  </Label>
                  <Input value={transparency?.contact_for_complaints || ''} onChange={e => setTransparency({ ...transparency, contact_for_complaints: e.target.value })} placeholder="reclamacoes@empresa.pt" />
                </div>
                <Button onClick={saveTransparency} disabled={savingTrans} data-testid="save-transparency-btn">
                  {savingTrans ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  Guardar Transparência
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Human Oversight (F8) */}
          <TabsContent value="oversight">
            <Card className="card-flat" data-testid="oversight-form">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Supervisão Humana (F8 — Art. 14)</CardTitle>
                  <span className="text-sm font-semibold text-indigo-600">{hOversight?.completeness_score ?? 0}% completo</span>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {[
                  { key: 'operator_training', label: 'Operadores receberam formação adequada para usar o sistema' },
                  { key: 'output_interpretation_aids', label: 'Existem auxiliares para interpretar os outputs do sistema' },
                  { key: 'automation_bias_mitigation', label: 'Estão implementadas medidas para mitigar viés de automação' },
                  { key: 'stop_button_available', label: 'Existe um "botão de paragem" acessível ao operador' },
                  { key: 'override_capability', label: 'Operador pode anular/sobrepor decisões do sistema' },
                  { key: 'explanation_to_subjects', label: 'Sujeitos a decisões automatizadas recebem explicação' },
                ].map(check => (
                  <label key={check.key} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors">
                    <Checkbox
                      checked={!!hOversight?.[check.key]}
                      onCheckedChange={c => setHOversight({ ...hOversight, [check.key]: !!c })}
                      data-testid={`oversight-${check.key}`}
                    />
                    <span className="text-sm text-slate-800">{check.label}</span>
                  </label>
                ))}
                <div className="space-y-2">
                  <Label>Detalhes da formação dos operadores</Label>
                  <Textarea rows={2} value={hOversight?.operator_training_details || ''} onChange={e => setHOversight({ ...hOversight, operator_training_details: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Operadores designados</Label>
                  <Input value={hOversight?.designated_operators || ''} onChange={e => setHOversight({ ...hOversight, designated_operators: e.target.value })} placeholder="Ex: Equipa RH (3 pessoas)" />
                </div>
                <div className="space-y-2">
                  <Label>Procedimentos de supervisão</Label>
                  <Textarea rows={2} value={hOversight?.procedures || ''} onChange={e => setHOversight({ ...hOversight, procedures: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>KPIs de monitorização</Label>
                  <Textarea rows={2} value={hOversight?.monitoring_kpis || ''} onChange={e => setHOversight({ ...hOversight, monitoring_kpis: e.target.value })} placeholder="Ex: % overrides, tempo médio de revisão" />
                </div>
                <Button onClick={saveOversight} disabled={savingOversight} data-testid="save-oversight-btn">
                  {savingOversight ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  Guardar Supervisão
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* EU Registry (F10) */}
          <TabsContent value="eu-registry">
            <Card className="card-flat" data-testid="eu-registry-form">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Registo EU Database (F10 — Art. 49)</CardTitle>
                  <Badge variant="outline" className={cn(
                    'text-xs',
                    euRegistry?.status === 'registered' ? 'bg-green-50 text-green-700 border-green-200' :
                    euRegistry?.status === 'submitted' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                    'bg-slate-100 text-slate-600 border-slate-200'
                  )} data-testid="eu-reg-status">{euRegistry?.status || 'draft'}</Badge>
                </div>
                <p className="text-sm text-slate-600">Apenas sistemas de alto risco têm obrigação de registo na base de dados da UE.</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nome do fornecedor</Label>
                    <Input value={euRegistry?.provider_name || ''} onChange={e => setEuRegistry({ ...euRegistry, provider_name: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>Contacto do fornecedor</Label>
                    <Input value={euRegistry?.provider_contact || ''} onChange={e => setEuRegistry({ ...euRegistry, provider_contact: e.target.value })} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Morada do fornecedor</Label>
                  <Input value={euRegistry?.provider_address || ''} onChange={e => setEuRegistry({ ...euRegistry, provider_address: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Tipo de avaliação de conformidade</Label>
                  <Input value={euRegistry?.conformity_assessment_type || ''} onChange={e => setEuRegistry({ ...euRegistry, conformity_assessment_type: e.target.value })} placeholder="self_assessment / notified_body" />
                </div>
                <div className="space-y-2">
                  <Label>Referência organismo notificado</Label>
                  <Input value={euRegistry?.notified_body_ref || ''} onChange={e => setEuRegistry({ ...euRegistry, notified_body_ref: e.target.value })} placeholder="Ex: NB-XXXX" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Data submissão</Label>
                    <Input type="date" value={euRegistry?.eu_db_submission_date || ''} onChange={e => setEuRegistry({ ...euRegistry, eu_db_submission_date: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>Referência EU DB</Label>
                    <Input value={euRegistry?.eu_db_reference || ''} onChange={e => setEuRegistry({ ...euRegistry, eu_db_reference: e.target.value })} placeholder="EU-AI-XXXXX" />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={saveEuReg} disabled={savingEuReg} data-testid="save-eu-reg-btn">
                    {savingEuReg ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                    Guardar
                  </Button>
                  <Button variant="default" onClick={submitEuReg} disabled={submittingEu || euRegistry?.status === 'submitted'} data-testid="submit-eu-reg-btn">
                    {submittingEu ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Globe className="h-4 w-4 mr-2" />}
                    {euRegistry?.status === 'submitted' ? 'Já submetido' : 'Submeter à EU Database'}
                  </Button>
                </div>
                <div className="text-xs text-slate-500 bg-amber-50 border border-amber-200 rounded p-3">
                  <strong>Nota:</strong> A integração real com a EU Database é mockada nesta versão (Art. 49 só será operacional após 2026). Configure <code>eu_db_endpoint</code> em Definições quando disponível.
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* GPAI */}
          {system.entity_type === 'gpai_model' && (
            <TabsContent value="gpai">
              <Card className="card-flat" data-testid="gpai-form">
                <CardHeader>
                  <CardTitle>Modelo GPAI (Arts. 51-56)</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Nome do modelo</Label>
                      <Input value={gpai?.model_name || ''} onChange={e => setGpai({ ...gpai, model_name: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                      <Label>FLOPs de treino</Label>
                      <Input type="number" value={gpai?.training_flops ?? ''} onChange={e => setGpai({ ...gpai, training_flops: e.target.value ? Number(e.target.value) : null })} data-testid="gpai-flops" />
                    </div>
                  </div>
                  {gpai?.systemic_risk && (
                    <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200" data-testid="gpai-systemic">
                      ⚠️ Risco Sistémico (Art. 55) — FLOPs {'>'} 10²⁵
                    </Badge>
                  )}
                  <div className="space-y-2">
                    <Label>Política de uso (URL)</Label>
                    <Input value={gpai?.use_policy || ''} onChange={e => setGpai({ ...gpai, use_policy: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>URL documentação fornecedor</Label>
                    <Input value={gpai?.provider_doc_url || ''} onChange={e => setGpai({ ...gpai, provider_doc_url: e.target.value })} />
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox id="provider_doc" checked={!!gpai?.provider_doc_available} onCheckedChange={c => setGpai({ ...gpai, provider_doc_available: !!c })} />
                    <Label htmlFor="provider_doc">Documentação técnica do fornecedor disponível</Label>
                  </div>
                  <div className="space-y-2">
                    <Label>Política de copyright</Label>
                    <Textarea rows={2} value={gpai?.copyright_policy || ''} onChange={e => setGpai({ ...gpai, copyright_policy: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>Testes adversariais / red-teaming</Label>
                    <Textarea rows={2} value={gpai?.adversarial_testing || ''} onChange={e => setGpai({ ...gpai, adversarial_testing: e.target.value })} />
                  </div>
                  <Button onClick={saveGpai} disabled={savingGpai} data-testid="save-gpai-btn">
                    {savingGpai ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                    Guardar GPAI
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>

      </div>
    </Layout>
  );
}
